import { apiClient } from '../../../api/client'
import { AccountsusersApi } from '../../../generated-types/apis/AccountsusersApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createUser } from '../../../utils/functions'
import { type User } from '../../../utils/types'

const log = new LogTool({ context: 'ProfileAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
function getUsersApi(): AccountsusersApi {
  return new AccountsusersApi(getApiConfiguration())
}

/**
 * Fetch the current user's profile
 * Note: Uses apiClient for custom /self/ endpoint
 * @param expand - Optional array of relations to expand (e.g., ['roles'])
 */
export async function fetchProfile(expand?: string[]): Promise<User> {
  log.info('Fetching profile')

  const url = expand?.length 
    ? `v1/accounts/users/self/?expand=${expand.join(',')}`
    : 'v1/accounts/users/self/'
  const response = await apiClient.get<any>(url)
  return createUser(response)
}

/**
 * Update the current user's profile
 * Note: Uses apiClient for custom /self/ endpoint
 */
export async function updateProfileApi(profileData: any): Promise<User> {
  log.info('Updating profile:', profileData)

  const response = await apiClient.put<any>('v1/accounts/users/self/', profileData)
  return createUser(response)
}

/**
 * Patch the current user's profile (partial update)
 * Note: Uses apiClient for custom /self/ endpoint
 */
export async function patchProfileApi(profileData: Partial<any>): Promise<User> {
  log.info('Patching profile:', profileData)

  const response = await apiClient.patch<any>('v1/accounts/users/self/', profileData)
  return createUser(response)
}

/**
 * Update user preferences
 * Note: Uses apiClient for custom preferences endpoint
 */
export async function updatePreferencesApi(preferences: any): Promise<any> {
  log.info('Updating preferences:', preferences)

  const response = await apiClient.patch<any>('v1/accounts/users/self/preferences/', preferences)
  return response
}

/**
 * Fetch user preferences
 * Note: Uses apiClient for custom preferences endpoint
 */
export async function fetchPreferencesApi(): Promise<any> {
  log.info('Fetching preferences')

  const response = await apiClient.get<any>('v1/accounts/users/self/preferences/')
  return response
}
